import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';

import { MatterService } from '../../../services/matters/matter.service';
import { AuthService } from '../../../services/common/auth.service';

@Component({
  selector: 'app-matter-status',
  templateUrl: './matter-status.component.html',
  styleUrls: ['./matter-status.component.css'],
})
export class MatterStatusComponent implements OnInit {
  /**
   * Matter info from parent component
   */
  @Input() matter_info: any;

  /**
   * Matter id from parent component
   */
  @Input() matter_id: any;

  /**
   * Matter status info
   * @type {any}
   */
  matter_status_info: any = {};

  /**
   * Indicates if status is loading
   * @type {boolean}
   */
  loading: boolean = true;

  /**
   * Information constructed from the status response
   * @type {any}
   */
  constructedInfo: any = {};

  /**
   * Constructor
   * @param {MatterService} private matterService Service to access matters controller in the backend
   * @param {AuthService} public authService Service to get authenticated download URLs
   */
  constructor(private matterService: MatterService, public authService: AuthService) {}

  // Component lifecycle methods

  /**
   * Called on component initialization
   */
  ngOnInit() {}

  /**
   * Called when variables of this component change
   * @param {SimpleChanges} changes Angular changes object with variables changed and their current values
   */
  ngOnChanges(changes: SimpleChanges) {
    if ((changes.matter_id && changes.matter_id.currentValue) || (changes.matter_info && changes.matter_info.currentValue)) {
      if (this.matter_info && this.matter_info.status_info) {
        this.matter_status_info = this.matter_info.status_info;
        this.loading = false;
      } else if (this.matter_id) this.loadStatus();
    }
  }

  /**
   * Balance constructed from matter status response
   */
  balance() {
    try {
      this.constructedInfo.balance = this.matter_status_info.overall[0][1].balance || 0;
    } catch (err) {
      this.constructedInfo.balance = 0;
    }
  }

  /**
   * Billings constructed from matter status response
   */
  billings() {
    try {
      this.constructedInfo.billings = this.matter_status_info.overall[0][1].billings || 0;
    } catch (err) {
      this.constructedInfo.billings = 0;
    }
  }

  /**
   * Charge constructed from matter status response
   */
  charge() {
    try {
      this.constructedInfo.charge = this.matter_status_info.overall[0][1].charge || 0;
    } catch (err) {
      this.constructedInfo.charge = 0;
    }
  }

  /**
   * Escrow cost constructed from matter status response
   */
  escrow_cost() {
    try {
      this.constructedInfo.escrow_cost = this.matter_status_info.overall[0][1].escrow_cost || 0;
    } catch (err) {
      this.constructedInfo.escrow_cost = 0;
    }
  }

  /**
   * Escrow fee constructed from matter status response
   */
  escrow_fee() {
    try {
      this.constructedInfo.escrow_fee = this.matter_status_info.overall[0][1].escrow_fee || 0;
    } catch (err) {
      this.constructedInfo.escrow_fee = 0;
    }
  }

  /**
   * Other escrow  constructed from matter status response
   */
  escrow_other() {
    try {
      this.constructedInfo.escrow_other = this.matter_status_info.overall[0][1].escrow_other || 0;
    } catch (err) {
      this.constructedInfo.escrow_other = 0;
    }
  }

  /**
   * Escrow total  constructed from matter status response
   */
  escrow_total() {
    try {
      this.constructedInfo.escrow_total = this.matter_status_info.overall[0][1].escrow || 0;
    } catch (err) {
      this.constructedInfo.escrow_total = 0;
    }
  }

  /**
   * Fee due constructed from matter status response
   */
  fee_due() {
    try {
      this.constructedInfo.fee_due = this.matter_status_info.overall[0][1].fee_due || 0;
    } catch (err) {
      this.constructedInfo.fee_due = 0;
    }
  }

  /**
   *  constructed from matter status response
   */
  first_expense() {
    try {
      this.constructedInfo.first_expense = this.matter_status_info.overall[0][1].expense || 0;
    } catch (err) {
      this.constructedInfo.first_expense = 0;
    }
  }

  /**
   * First expense difference constructed from matter status response
   */
  first_expense_difference() {
    this.constructedInfo.first_expense_difference = this.constructedInfo.first_expense - this.constructedInfo.first_expense_payment;
  }

  /**
   * First expense payment constructed from matter status response
   */
  first_expense_payment() {
    try {
      this.constructedInfo.first_expense_payment = this.matter_status_info.overall[0][1].expense_payment || 0;
    } catch (err) {
      this.constructedInfo.first_expense_payment = 0;
    }
  }

  /**
   * Fixed fee constructed from matter status response
   */
  fixed_fee() {
    try {
      this.constructedInfo.fixed_fee = this.matter_status_info.overall[0][1].fixed_fee || 0;
    } catch (err) {
      this.constructedInfo.fixed_fee = 0;
    }
  }

  /**
   * Load matter status
   */
  loadStatus() {
    let scope: MatterStatusComponent = this;
    this.matterService.getMatterStatusInfo(this.matter_id).then((response_json) => {
      if (!response_json) {
        console.log('Bad matter status result, loading status will be attempted again in 20 seconds');
        response_json = [];
        window.setTimeout(() => {
          if (scope && scope.loadStatus) scope.loadStatus();
        }, 20000);
        throw `Matter status not returned for matter id ${this.matter_id}, response JSON is undefined`;
      }
      this.matter_status_info = response_json;
      this.matter_status_info.retainerIsNaN = isNaN(Number(this.matter_status_info.retainer));
      this.loading = false;
      this.escrow_fee();
      this.escrow_cost();
      this.escrow_other();
      this.escrow_total();
      this.first_expense();
      this.first_expense_payment();
      this.first_expense_difference();
      this.time();
      this.fixed_fee();
      this.charge();
      this.fee_due();
      this.sum_of_charges();
      this.sum_of_payments();
      this.billings();
      this.balance();
    });
  }

  /**
   * Sum of charges constructed from matter status response
   */
  sum_of_charges() {
    try {
      this.constructedInfo.sum_of_charges = this.matter_status_info.overall[0][1].sum_of_charges || 0;
    } catch (err) {
      this.constructedInfo.sum_of_charges = 0;
    }
  }

  /**
   * Sum of payments constructed from matter status response
   */
  sum_of_payments() {
    try {
      this.constructedInfo.sum_of_payments = this.matter_status_info.overall[0][1].sum_of_payments || 0;
    } catch (err) {
      this.constructedInfo.sum_of_payments = 0;
    }
  }

  /**
   * Time constructed from matter status response
   */
  time() {
    try {
      this.constructedInfo.time = this.matter_status_info.overall[0][1].time || 0;
    } catch (err) {
      this.constructedInfo.time = 0;
    }
  }
}
